import { useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Copy, Check, ChevronDown, ChevronRight } from "lucide-react";
import { TRIGGER_SUBTYPES, WEBHOOK_METHODS, type TriggerSubtype } from "@proclaw/shared";
import { m365Api } from "@/api/m365";
import { workflowsApi } from "@/api/workflows";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { Field, TextInput, Select, WorkflowToggle } from "./form-primitives";

const SUBTYPE_LABELS: Record<TriggerSubtype, string> = {
  manual: "Manual — user clicks Run",
  schedule: "Schedule — cron expression",
  webhook: "Webhook — external HTTP call",
  channel_message: "Channel message — new message posted",
  email_received: "Outlook email received",
};

/**
 * Config form for a Trigger node. Editable only when admin; read-only
 * otherwise (caller passes `readOnly`).
 */
export function TriggerConfig({
  data,
  onChange,
  readOnly,
  workflowId,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  workflowId?: string;
}) {
  const { tenantId } = useTenant();
  const subtype = (data.subtype as TriggerSubtype) ?? "manual";
  const [attachmentsOptionAdded, setAttachmentsOptionAdded] = useState(
    data.includeAttachments === true,
  );
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const mailboxQueries = useQueries({
    queries: agents.map((agent) => ({
      queryKey: ["agent-mailbox", agent.id],
      queryFn: () => m365Api.getAgentMailbox(tenantId, agent.id),
      enabled: subtype === "email_received" && Boolean(tenantId),
      staleTime: 60_000,
    })),
  });
  const update = (patch: Record<string, unknown>) => onChange({ ...data, ...patch });
  const selectedAgentId = (data.agentId as string) ?? "";
  const { data: outlookStatuses = [] } = useQuery({
    queryKey: ["workflow-outlook-trigger-status", workflowId],
    queryFn: () => workflowsApi.outlookTriggerStatus(workflowId!),
    enabled: subtype === "email_received" && Boolean(workflowId),
    refetchInterval: 30_000,
  });
  const verifiedMailboxes = agents.flatMap((agent, index) => {
    const mailbox = mailboxQueries[index]?.data;
    return mailbox?.scopeStatus === "rbac_verified"
      ? [{ agent, mailbox }]
      : [];
  });
  const selectedAgent = agents.find((agent) => agent.id === selectedAgentId);
  const selectedIsVerified = verifiedMailboxes.some(
    ({ agent }) => agent.id === selectedAgentId,
  );
  const selectedOutlookStatus = outlookStatuses.find(
    (status) => status.agentId === selectedAgentId,
  );
  const mailboxOptions = [
    {
      value: "",
      label:
        verifiedMailboxes.length > 0
          ? "— select an agent mailbox —"
          : mailboxQueries.some((query) => query.isLoading)
            ? "Loading verified mailboxes..."
            : "No verified agent mailboxes",
    },
    ...verifiedMailboxes.map(({ agent, mailbox }) => ({
      value: agent.id,
      label: `${agent.name} · ${mailbox.mailboxUpn}`,
    })),
    ...(selectedAgentId && !selectedIsVerified
      ? [
          {
            value: selectedAgentId,
            label: `${selectedAgent?.name ?? "Selected agent"} · mailbox not verified`,
          },
        ]
      : []),
  ];
  return (
    <div className="space-y-4">
      <Field label="Trigger type">
        <Select
          value={subtype}
          onChange={(v) => update({ subtype: v as TriggerSubtype })}
          disabled={readOnly}
          options={TRIGGER_SUBTYPES.map((s) => ({ value: s, label: SUBTYPE_LABELS[s] }))}
        />
      </Field>

      {subtype === "schedule" && (
        <>
          <Field label="Cron expression">
            <TextInput
              value={(data.cronExpression as string) ?? ""}
              onChange={(v) => update({ cronExpression: v })}
              placeholder="0 9 * * *"
              readOnly={readOnly}
              mono
            />
            <p className="mt-1 text-[11px] text-text-muted">
              Standard 5-field cron (minute hour day-of-month month day-of-week).
            </p>
          </Field>
          <Field label="Timezone">
            <TextInput
              value={(data.timezone as string) ?? "UTC"}
              onChange={(v) => update({ timezone: v })}
              placeholder="UTC"
              readOnly={readOnly}
            />
          </Field>
        </>
      )}

      {subtype === "webhook" && (
        <>
          <Field label="HTTP method">
            <Select
              value={((data.method as string) ?? "POST").toUpperCase()}
              onChange={(v) => update({ method: v })}
              disabled={readOnly}
              options={WEBHOOK_METHODS.map((m) => ({ value: m, label: m }))}
            />
            <p className="mt-1 text-[11px] text-text-muted">
              GET uses the URL query string. POST, PUT, PATCH, and DELETE use
              the JSON request body. Keep GET values non-sensitive.
            </p>
          </Field>
          <WebhookFields
            workflowId={workflowId}
            webhookId={(data.webhookId as string) ?? ""}
            method={((data.method as string) ?? "POST").toUpperCase()}
          />
        </>
      )}

      {subtype === "email_received" && (
        <>
          <Field label="Agent mailbox">
            <Select
              value={selectedAgentId}
              onChange={(value) => update({ agentId: value || undefined })}
              disabled={readOnly}
              options={mailboxOptions}
            />
            <p className="mt-1 text-[11px] text-text-muted">
              Watches this agent&apos;s verified Outlook Inbox. Configure or
              verify the mailbox in the agent settings if it is unavailable.
            </p>
          </Field>
          <Field label="From (optional)">
            <TextInput
              value={(data.from as string) ?? ""}
              onChange={(value) => update({ from: value.trim() || undefined })}
              placeholder="sender@example.com"
              readOnly={readOnly}
            />
            <p className="mt-1 text-[11px] text-text-muted">
              Exact sender email address. Leave empty to accept any sender.
            </p>
          </Field>
          {selectedOutlookStatus && (
            <div className="rounded-md border border-border bg-background px-3 py-2 text-xs">
              <p className="text-text-primary">
                Subscription: {selectedOutlookStatus.status}
              </p>
              {selectedOutlookStatus.lastError && (
                <p className="mt-1 text-danger">{selectedOutlookStatus.lastError}</p>
              )}
            </div>
          )}
          <Field label="Subject contains (optional)">
            <TextInput
              value={(data.subjectContains as string) ?? ""}
              onChange={(value) =>
                update({ subjectContains: value || undefined })
              }
              placeholder="Invoice"
              readOnly={readOnly}
            />
          </Field>
          <div>
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-text-muted">
              Others
            </span>
            <button
              type="button"
              onClick={() => setAttachmentsOptionAdded((open) => !open)}
              aria-expanded={attachmentsOptionAdded}
              disabled={readOnly}
              className="flex h-9 w-full items-center justify-between rounded-md border border-border bg-background px-3 text-left text-sm text-text-primary outline-none transition-colors hover:border-accent focus:border-accent disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span>More</span>
              {attachmentsOptionAdded ? (
                <ChevronDown size={15} className="text-text-muted" />
              ) : (
                <ChevronRight size={15} className="text-text-muted" />
              )}
            </button>
          </div>
          {attachmentsOptionAdded && (
            <WorkflowToggle
              label="Include attachments"
              description="Adds attachment details and secure download paths to the workflow. File content stays in Outlook until a step requests it."
              checked={data.includeAttachments === true}
              disabled={readOnly}
              onChange={(checked) =>
                update({ includeAttachments: checked || undefined })
              }
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Surfaces the live incoming-webhook URL + secret for a webhook-triggered
 * workflow. The secret (`wh_…`) is provisioned server-side on first save and
 * embedded in the URL path; the route authenticates on it (constant-time).
 * The URL is same-origin with the platform the user is viewing, so we build
 * it from window.location.origin.
 */
function WebhookFields({
  workflowId,
  webhookId,
  method = "POST",
}: {
  workflowId?: string;
  webhookId: string;
  method?: string;
}) {
  const [copied, setCopied] = useState(false);
  const url =
    webhookId && workflowId
      ? `${window.location.origin}/api/workflows/${workflowId}/webhook/${webhookId}`
      : "";

  const copy = () => {
    if (!url) return;
    void navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!url) {
    return (
      <Field label="Webhook URL">
        <TextInput
          value="(generated when you save the workflow)"
          onChange={() => undefined}
          readOnly
          mono
        />
        <p className="mt-1 text-[11px] text-text-muted">
          Save the workflow once to generate its unique webhook URL and secret.
        </p>
      </Field>
    );
  }

  return (
    <Field label="Webhook URL">
      <div className="flex items-start gap-1.5">
        <div className="min-w-0 flex-1">
          <TextInput value={url} onChange={() => undefined} readOnly mono />
        </div>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy webhook URL"
          title={copied ? "Copied" : "Copy URL"}
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded border border-border text-text-muted transition-colors hover:bg-background hover:text-text-primary"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
      <p className="mt-1.5 text-[11px] leading-relaxed text-text-muted">
        Send an HTTP <span className="font-mono">{method}</span> to this URL to fire the workflow;
        {method === "GET"
          ? " the URL query string is handed to the agent as the trigger input."
          : " the JSON body is handed to the agent as the trigger input."}{" "}
        The secret in the URL authenticates the call — treat the whole URL like a password and share
        it only with trusted systems. The webhook fires only while the workflow status is{" "}
        <strong className="text-text-primary">Active</strong>; set it inactive to disable it
        instantly.
      </p>
    </Field>
  );
}
